export default function (options = {}) {
    if (typeof options.ctx === 'undefined') {
        throw 'SVGram Error: no canvas element provided';
    }

    const ctx = options.ctx;
    var startTime = new Date();
    var scale = options.scale || 1;
    var offsetX = options.offsetX || 0;
    var offsetY = options.offsetY || 0;
    var textPointer = { x: 0, y: 0 };
    var currentFontSize = 999;

    options = options || {};

    /**
     * Returns initial value multiplied by scale
     * @params  {number} value
     * @returns {number}
     */
    var getProperCoord = function (value) {
        value = value * scale;
        return options.processCoord ? options.processCoord(value) : value;
    };

    /**
     * Performs simple action on canvas's rendering context
     * @params  {CanvasRenderingContext2D} ctx
     * @params  {Object} item
     * @params  {string} item.name  // Method name
     * @params  {Array}  item.args  // Arguments
     */
    var simpleAction = function (ctx, item) {
        var action = item.action;
        var args = item.args;

        if (typeof ctx[action] !== 'function') {
            return;
        }

        if (args && args.length === +args.length) {
            ctx[action].apply(ctx, args.map(getProperCoord));
        } else {
            ctx[action]();
        }
    };

    var handlers = {
        // Transforms

        rotate: function (ctx, item) {
            ctx.rotate(item.args[0]);
        },
        scale: function (ctx, item) {
            ctx.scale.apply(ctx, item.args);
        },
        transform: function (ctx, item) {
            ctx.transform.apply(
                ctx,
                item.args.map(function (value, i) {
                    return i >= 4 ? getProperCoord(value) : value;
                }),
            );
        },
        translate: function (ctx, item) {
            simpleAction(ctx, item);
        },

        // Properties

        setProperty: function (ctx, item) {
            var property = item.name;
            var value = (function (value) {
                switch (property) {
                    case 'font':
                        value.scale(scale);
                        currentFontSize = value.getSize();
                        return value.toString();
                    case 'strokeWidth':
                        return value * (options.preserveLineWidthWhenScaling ? 1 : scale);
                    default:
                        return value;
                }
            })(item.value);

            ctx[this.getCTXPropertyName(item.name)] = value;
        },

        // Shapes

        fill: function (ctx, item) {
            ctx.fill();
        },
        stroke: function (ctx, item) {
            ctx.stroke();
        },
        save: function (ctx, item) {
            ctx.save();
        },
        restore: function (ctx, item) {
            ctx.restore();
        },
        drawCircle: function (ctx, item) {
            var x = getProperCoord(item.x);
            var y = getProperCoord(item.y);
            var r = getProperCoord(item.r);
            ctx.arc(x, y, r, 0, Math.PI * 2);
        },
        drawEllipse: function (ctx, item) {
            var x = getProperCoord(item.x);
            var y = getProperCoord(item.y);
            var rx = getProperCoord(item.rx);
            var ry = getProperCoord(item.ry);
            if (ctx.ellipse) {
                ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
            } else {
                ctx.save();
                ctx.translate(x, y);
                ctx.scale(rx, ry);
                ctx.arc(0, 0, 1, 0, Math.PI * 2, 0);
                ctx.restore();
            }
        },
        fillRect: function (ctx, item) {
            simpleAction(ctx, item);
        },
        strokeRect: function (ctx, item) {
            simpleAction(ctx, item);
        },

        // Paths

        path: function (ctx, item) {
            let p = new Path2D(item.segments.map((item) => item.getValue(scale)).join(' '));
            item.shouldFill && ctx.fill(p);
            item.shouldStroke && ctx.stroke(p);
        },

        beginPath: function (ctx, item) {
            ctx.beginPath();
        },
        closePath: function (ctx, item) {
            ctx.closePath();
        },
        moveTo: function (ctx, item) {
            simpleAction(ctx, item);
        },
        lineTo: function (ctx, item) {
            simpleAction(ctx, item);
        },
        bezierCurveTo: function (ctx, item) {
            simpleAction(ctx, item);
        },
        quadraticCurveTo: function (ctx, item) {
            simpleAction(ctx, item);
        },
        arc: function (ctx, item) {
            ctx.arc.apply(
                ctx,
                item.args.map(function (item, i) {
                    return i < 3 ? getProperCoord(item) : item;
                }),
            );
        },

        // Text

        /**
         * Sets text-pointer to coords
         */
        moveTextPointer: function (ctx, item) {
            textPointer.x = getProperCoord(item.x);
            textPointer.y = getProperCoord(item.y);
        },
        /**
         * Shifts text-pointer relatively to current position
         */
        shiftTextPointer: function (ctx, item) {
            textPointer.x += getProperCoord(item.x);
            textPointer.y += getProperCoord(item.y);
        },
        /**
         * Draws text
         */
        insertText: function (ctx, item) {
            var value = item.value.trim() + ' ';
            var shouldDrawText = options.minVisibleFontSize
                ? currentFontSize / scale >= options.minVisibleFontSize
                : true;
            if (shouldDrawText) {
                item.fill && ctx.fillText(value, textPointer.x, textPointer.y);
                item.stroke && ctx.strokeText(value, textPointer.x, textPointer.y);
            }
            // Updating text-pointer x coord
            textPointer.x += ctx.measureText(value).width;
        },
    };

    // Applying offsets

    ctx.translate(offsetX, offsetY);

    // Applying instructions
    this.instructions.forEach((item) => {
        if (handlers[item.action]) {
            handlers[item.action].call(this, ctx, item);
        }
    });

    // Reverting offsets

    ctx.translate(-offsetX, -offsetY);
}
